import * as React from "react";
import { Toaster as Sonner } from "sonner";

type ToasterProps = React.ComponentProps<typeof Sonner>;

// Sonner wants an explicit theme (its "system" default tracks the OS, not the
// app). The app signals dark mode by stamping `.dark` on <html> (theme.ts /
// the index.html pre-paint script), so subscribe to that class here — this
// keeps the wrapper self-contained instead of threading theme state through
// every app shell that mounts a <Toaster />.
function subscribeToRootClass(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class"],
  });
  return () => observer.disconnect();
}

function isDarkTheme(): boolean {
  return document.documentElement.classList.contains("dark");
}

const Toaster = ({ ...props }: ToasterProps) => {
  const isDark = React.useSyncExternalStore(
    subscribeToRootClass,
    isDarkTheme,
    // Server snapshot: no DOM to read, default to light.
    () => false,
  );

  return (
    <Sonner
      theme={isDark ? "dark" : "light"}
      className="toaster group"
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
      {...props}
    />
  );
};

export { Toaster };
export { toast } from "sonner";
